import type { ReactNode } from "react";
import { useMemo } from "react";

import type { FinanceMonthPoint } from "@busright/data-contract";
import { COLORS, EChart, type EChartOption } from "@busright/ui";

// =================================================================================================
// FinArOverTimeChart — the outstanding-vs-paid MoM area graph: the credit-hold story (watch the AR
// gap grow or close). NOTE on color: `finance.arOutstandingUsd` / `finance.arPaidUsd` are real-unit
// USD FACTS (isHealthScore=false in the registry), and tokens.css is explicit that RAG green/amber/
// red is "reserved EXCLUSIVELY for health/risk status — never for a financial metric". So this does
// NOT use RAG.CRITICAL/RAG.OK even though "outstanding vs paid" reads intuitively as red/green — it
// uses two ends of the neutral/brand sequential ramp instead (accent = outstanding, a cool sequential
// stop = paid), keeping the "gap" story visible without breaking the color law.
// =================================================================================================

const OUTSTANDING_COLOR = COLORS.accent;
const PAID_COLOR = "#5566a0"; // --br-seq-3 — cool sequential stop, reads as the "paid off" side.

export interface FinArOverTimeChartProps {
  readonly series: readonly FinanceMonthPoint[];
  readonly height?: number;
}

export const FinArOverTimeChart = ({
  series,
  height = 260,
}: FinArOverTimeChartProps): ReactNode => {
  const option = useMemo<EChartOption>(
    () => ({
      grid: { left: 56, right: 16, top: 28, bottom: 28 },
      legend: {
        top: 0,
        textStyle: { color: COLORS.textSecondary, fontSize: 11 },
        data: ["Outstanding", "Paid"],
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: COLORS.surface2,
        borderColor: COLORS.border,
        textStyle: { color: COLORS.textPrimary },
        valueFormatter: (v: number | string) => `$${Number(v).toLocaleString("en-US")}`,
      },
      xAxis: {
        type: "category",
        data: series.map((p) => p.month),
        axisLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 10 },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: COLORS.border } },
        axisLabel: { color: COLORS.textMuted, fontSize: 10 },
      },
      series: [
        {
          name: "Outstanding",
          type: "line",
          areaStyle: { color: OUTSTANDING_COLOR, opacity: 0.22 },
          lineStyle: { color: OUTSTANDING_COLOR, width: 2 },
          itemStyle: { color: OUTSTANDING_COLOR },
          symbol: "circle",
          symbolSize: 5,
          data: series.map((p) => p.outstandingUsd),
        },
        {
          name: "Paid",
          type: "line",
          areaStyle: { color: PAID_COLOR, opacity: 0.22 },
          lineStyle: { color: PAID_COLOR, width: 2 },
          itemStyle: { color: PAID_COLOR },
          symbol: "circle",
          symbolSize: 5,
          data: series.map((p) => p.paidUsd),
        },
      ],
    }),
    [series],
  );

  return <EChart option={option} height={height} />;
};
